import authApi from 'api/authApi';
import feApi from 'api/feApi';
import 'assets/styles/keenpages/login-1.css';
import AuthHelper from 'features/Auth/authHelper';
import ForgotPasswordForm from 'features/Auth/components/ForgotPasswordForm';
import ResetPasswordForm from 'features/Auth/components/ResetPasswordForm';
import SignInForm from 'features/Auth/components/SignInForm';
import SignUpForm from 'features/Auth/components/SignUpForm';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import { AppDialogHelper } from 'general/components/AppDialog';
import AppResource from 'general/constants/AppResource';
import AppRoute from 'general/constants/AppRoute';
import AppConstants from 'general/constants/AppConstants';
import PreferenceKeys from 'general/constants/PreferenceKeys';
import ToastHelper from 'general/helpers/ToastHelper';
import RoleUtils from 'general/utils/RoleUtils';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';

function AuthBaseScreen(props) {
  // MARK: --- Params ---
  const router = useRouter();
  const { t } = useTranslation();
  const { isLoggingIn, user } = useSelector((state) => state.auth);
  const isResetPasswordRoute =
    router.location.pathname.replace(/\/+$/, '') === `${AppRoute.auth}/reset-password`;

  // MARK: --- Functions ---
  const handleNotFoundEmailDialog = (onCloseCallback) => {
    AppDialogHelper.show(
      t('Cảnh báo'),
      t('Email này chưa đăng ký tài khoản'),
      [
        {
          title: t('Tôi đã hiểu'),
          type: 'neutral',
          onClick: () => {
            AppDialogHelper.hide();
            if (onCloseCallback) onCloseCallback();
          },
        },
      ],
      false,
      AppResource.images.dialogIcons.dialogNoticeEmail
    );
  };

  async function handleSubmitForgotPassword(values) {
    try {
      let res;
      try {
        res = await feApi.requestForgotPassword(values.email);
      } catch (customerError) {
        res = await authApi.forgotPassword(values.email);
      }
      const { result } = res;
      if (result === 'success') {
        ToastHelper.showSuccess(
          'Vui lòng kiểm tra hòm thư và làm theo hướng dẫn để thực hiện đặt lại mật khẩu'
        );
        return 'success';
      }
    } catch (error) {
      const msg = error?.response?.data?.reason ?? '';
      if (msg === 'Không tìm thấy tài khoản') {
        handleNotFoundEmailDialog();
      } else {
        ToastHelper.showError(msg || 'Đã xảy ra lỗi, vui lòng thử lại sau');
      }
      return 'error';
    }
  }


  function handleCancelForgotPassword() {
    router.navigate(-1);
  }

  const handleRegisterSuccessDialog = (canSignInImmediately = false, email = '') => {
    AppDialogHelper.show(
      t('Đăng ký thành công'),
      canSignInImmediately
        ? t('Đăng ký khách hàng/CTV thành công. Bạn có thể đăng nhập ngay.')
        : t('Đăng ký tài khoản thành công, vui lòng kiểm tra hòm thư để kích hoạt tài khoản'),
      [
        {
          title: t('Đóng'),
          type: 'neutral',
          onClick: () => {
            AppDialogHelper.hide();
          },
        },
        {
          title: t('signIn'),
          type: 'success',
          onClick: async () => {
            AppDialogHelper.hide();
            const signInUrl = email
              ? `${AppRoute.auth}/sign-in?email=${encodeURIComponent(email)}`
              : `${AppRoute.auth}/sign-in`;
            router.navigate(signInUrl);
          },
        },
      ],
      false,
      AppResource.images.dialogIcons.dialogSuccess
    );
  };

  const handleSubmitSignUp = async (values) => {
    try {
      const isCustomer = values?.primaryRole === AppConstants.userRole.driver;
      const response = isCustomer
        ? await feApi.register(values)
        : await DealerHelper.callApiRegisterAgencyAccount(values);
      const { result, reason, detail, requiresEmailVerification } = response || {};
      if (result === 'success') {
        handleRegisterSuccessDialog(
          isCustomer || requiresEmailVerification === false,
          values?.email
        );
      } else {
        ToastHelper.showError(reason || detail || t('Đăng ký tài khoản thất bại'));
      }
    } catch (error) {
      const errorData = error?.response?.data;
      ToastHelper.showError(
        errorData?.reason ||
        errorData?.detail ||
        error?.message ||
        t('Đăng ký tài khoản thất bại')
      );
    }
  };

  function handleCancelSignUp() {
    router.navigate(-1);
  }

  const handleSignIn = (params) => {
    AuthHelper.callApiLogIn(params);
  };

  // MARK: --- Hooks ---
  useEffect(() => {
    if (isResetPasswordRoute) return;

    if (!isLoggingIn && !_.isEmpty(user)) {
      const savedUiMode = localStorage.getItem(PreferenceKeys.savedUiMode);
      router.navigate(
        RoleUtils.isCustomerUiRole(user?.role, savedUiMode)
          ? AppRoute.customerHome
          : RoleUtils.getCmsHomeRoute(user, RoleUtils.getAgencyRoles(user))
      );
    }
  }, [isLoggingIn, isResetPasswordRoute, user]);

  return (
    <div className="d-flex flex-column flex-root overflow-hidden">
      <div className="bg-white d-flex flex-column flex-column-fluid flex-lg-row login login-1 login-signin-on min-vh-100">
        {/* aside */}
        <div
          className="login-aside d-flex flex-column"
          style={{
            background: 'linear-gradient(135deg, #0B2E1C 0%, #123D26 55%, #0B2E1C 100%)',
          }}
        >
          {/* aside top */}
          <div className="d-flex flex-column h-100">
            <div className="h-50 d-flex align-items-end justify-content-center mb-5">
              <a href="#">
                <img alt="logo" src={AppResource.icons.evg.evgGroupLogoWhite} className="h-50px" />
              </a>
            </div>
            <div
              className="aside-img d-flex w-100 flex-row-fluid bgi-no-repeat bgi-position-y-bottom bgi-position-x-bottom"
              style={{
                backgroundImage: `url(${router.location.pathname === `${AppRoute.auth}/sign-in`
                  ? AppResource.images.evg.vehicleBg
                  : AppResource.images.evg.motorBg
                  })`,
                backgroundSize: '100% auto',
              }}
            ></div>
          </div>
        </div>

        {/* content */}
        <div className="login-content flex-row-fluid d-flex flex-column justify-content-center position-relative overflow-auto p-7 mx-auto">
          {/* content body */}
          <div className="d-flex flex-column-fluid flex-center">
            <>
              <Routes>
                <Route path="" element={<Navigate to="sign-in" />} />
                <Route
                  path="sign-in"
                  element={
                    <SignInForm
                      onSignIn={(params) => {
                        handleSignIn(params);
                      }}
                    />
                  }
                />
                <Route
                  path="sign-up-sale"
                  element={
                    <SignUpForm
                      role="SALE"
                      onSubmit={handleSubmitSignUp}
                      onCancel={handleCancelSignUp}
                    />
                  }
                />
                <Route
                  path="sign-up-sale-only"
                  element={
                    <SignUpForm
                      role="SALE"
                      onSubmit={handleSubmitSignUp}
                      onCancel={handleCancelSignUp}
                      isSaleOnly={true}
                    />
                  }
                />
                <Route
                  path="sign-up"
                  element={
                    <SignUpForm
                      onSubmit={handleSubmitSignUp}
                      onCancel={handleCancelSignUp}
                    />
                  }
                />
                <Route
                  path="sign-up-investor"
                  element={
                    <SignUpForm
                      role="INVESTOR"
                      onSubmit={handleSubmitSignUp}
                      onCancel={handleCancelSignUp}
                    />
                  }
                />
                <Route
                  path="sign-up-group"
                  element={
                    <SignUpForm
                      role="GROUP"
                      onSubmit={handleSubmitSignUp}
                      onCancel={handleCancelSignUp}
                    />
                  }
                />
                <Route
                  path="forgot-password"
                  element={
                    <ForgotPasswordForm
                      onSubmit={handleSubmitForgotPassword}
                      onCancel={handleCancelForgotPassword}
                    />
                  }
                />
                <Route path="reset-password" element={<ResetPasswordForm />} />
              </Routes>
            </>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthBaseScreen;
